<template>
  <div
    v-if="props.item"
    ref="videoelement"
    :style="{ borderRadius: `${currentBorderRadius[0]}px ${currentBorderRadius[1]}px ${currentBorderRadius[2]}px ${currentBorderRadius[3]}px` }"
    :class="{
      cursor: props.defaultCursor,
      'video _no-hover': !shouldHoverAnimation,
      'video hoverover hover-disabled': props.bare && !shouldHoverAnimation,
      'video hoverover': shouldHoverAnimation && !props.bare
    }"
    @click="clickAction()"
  >
    <ImageComponent :source="thumbnailSource" class="thumbnail" :class="{ _selected: props.selectOnly && isSelected, invisible: playing }" :border-radius="currentBorderRadius" />

    <LoadingSpinner class="loading" :class="{ visible: !playing && showPlayer }" :size="'small'" />
    <figure v-show="videoUrl.length > 0 && showPlayer" class="video-player">
      <video
        ref="videoplayer" autoplay muted loop
        class="video-player" :class="{ cover: isCover }" @click.stop="clickAction()"
      />
      <figcaption :class="{ jump: progressJump }">
        <progress
          id="progress" ref="progressbar" class="progress" max="100"
          value="0"
        >
          Progress
        </progress>
      </figcaption>
    </figure>
    <div v-if="!props.removeLabels" class="labels" :class="{ bare: bare || props.removeAddButton }">
      <GeoRestrictions v-if="props.geoRestrictions && displayGeoRestrictions" :countries-allow="props.item.countries_allow" :countries-disallow="props.item.countries_disallow" :layout="'pill'" />
      <VideoComponentCategories
        v-if="!props.geoRestrictions && !props.withBottom && props.item.categories"
        :categories="props.item.categories"
        class="categories"
        :class="cSize.breakpointsAdditive"
        is-on-video-thumb
      />
      <NewLabel v-if="showNewLabel && isNewVideo" />
      <div class="left-tags">
        <VideoAiTag v-if="props.showAiGeneratedTag && isAiGenerated" />
        <VideoLayoutOrientation v-if="showOrientation" :is-vertical="props.item.is_vertical" :tag-colors="TagColors.Black" />
        <VideoNotPairedTag v-if="showNotPairedTag && !props.item.is_paired_to_page" />
      </div>
      <Label
        v-if="showPendingTag" class="pending-label" :text="$t('generate-ai-pending-approval')" :color="LabelColors.Yellow"
        :size="LabelSizes.Small"
      />
    </div>
    <div v-if="threeDots" class="add-to-playlist-btn">
      <DropdownComponent v-if="threeDots" @click.stop>
        <template #trigger>
          <img src="@/assets/playlists/playlist/menu.svg">
        </template>
        <template #content="scopeProps">
          <div
            class="dropdown-item"
            @click.stop="
              () => {
                scopeProps.close()
                clickAction()
              }
            "
          >
            <IconWrapper class="dropdown-item-icon" data-testid="edit-icon" :name="'editIconBodyText'" />
            <span>{{ $t('content-video-edit') }}</span>
          </div>
          <div
            v-if="checkPermission('widgets')"
            class="dropdown-item"
            @click.stop="
              () => {
                scopeProps.close()
                detailsEmbed()
              }
            "
          >
            <IconWrapper class="dropdown-item-icon" :name="'embedIcon'" />
            <span>{{ $t('content-video-embed') }}</span>
          </div>
          <div
            class="dropdown-item"
            @click.stop="
              () => {
                scopeProps.close()
                openDeleteVideo()
              }
            "
          >
            <IconWrapper class="dropdown-item-icon" :name="'deleteIconBodyText'" />
            <span>{{ $t('content-video-delete') }}</span>
          </div>
        </template>
      </DropdownComponent>
    </div>
    <div v-else-if="!props.selectOnly && !creatingPlaylist" class="add-to-playlist-btn">
      <VideoActionButton
        v-if="props.showDownloadButton && canDownload"
        :text="$t('download-video')"
        :icon="isDownloading ? IconLoaderWhite : IconWhiteDownload"
        :class-list="'darkgray'"
        :select-only="false"
        :is-loading="isDownloading"
        @click="onDownloadClick"
      />
      <VideoActionButton
        v-if="!props.removeEmbedButton && !props.removeAddButton && !creatingPlaylist"
        :text="$t('embed-video')"
        :icon="IconEmbed"
        :class-list="'darkgray'"
        :select-only="false"
        @click="detailsEmbed"
      />
      <VideoActionButton
        v-if="!props.selected && !isSelected && !props.removeAddButton"
        :text="$t('add-to-playlist')"
        :icon="IconAdd"
        :class-list="'red'"
        :select-only="false"
        @click="onPlaylistAction"
      />
    </div>
    <div v-else-if="props.selectOnly || (creatingPlaylist && isCreatingPlaylistOnAddVideoState)" class="add-to-playlist-btn">
      <VideoActionButton
        v-if="!props.removeAddButton"
        :text="isSelected ? $t('remove-from-playlist') : $t('add-to-playlist')"
        :icon="isSelected ? IconRemove : IconAdd"
        :class-list="'red'"
        :select-only="true"
        @click="onPlaylistAction"
      />
    </div>
    <div v-if="showTime" class="video-duration-container" :class="cSize.breakpointsAdditive">
      {{ playingtime }}
    </div>
    <div v-if="props.showTopText" class="video-info-container">
      <div dir="auto" class="title top" :class="cSize.breakpointsAdditive">
        <span class="title-text">
          {{ props.item.title }}
        </span>
      </div>
      <div class="author top" :class="cSize.breakpointsAdditive">
        <div class="author-container">
          <div v-if="isNotVideosPath && isNotLibraryChannelPath && isNotFoldersPath && isNotPendingAiGenerated" class="author-wrapper">
            <span v-if="props.item.ownership">{{ t('own') }}</span>
            <span v-else class="author-by">
              {{ $t('library-video-by') }}
              <NewTooltip placement="top" :text-length="12" :content="props.item.organization_name" class="author-name" />
            </span>
            <span class="bullet-separator">●</span>
          </div>
          <span class="duration">
            {{ getDateFormat(props.item.upload_date) }}
          </span>
          <!-- TODO change to v-for when adding views -->
          <!-- <Social v-if="!isLibraryPath" :type="SocialType.Views" :variant="SocialVariant.cardTop" :total="props.item.views" /> -->
          <Social v-if="!isLibraryPath" :type="SocialType.Likes" :variant="SocialVariant.cardTop" :total="props.item.likes" />
          <Social v-if="!isLibraryPath" :type="SocialType.Shares" :variant="SocialVariant.cardTop" :total="props.item.shares" />
        </div>
      </div>
    </div>
    <div v-if="props.withBottom" class="bottom-extra-video-info">
      <div class="video-extra-details">
        <div v-if="isNotVideosPath">
          <ProfileImage :size="1" :custom-width-height="'32px'" :profile-image="profileImage" @click="handleOrganizationClicked" />
        </div>
        <div class="video-extra-details-title-author-container">
          <div dir="auto" class="title-text" :style="!withTitleEvents ? 'pointer-events: none' : ''">
            <NewTooltip :content="props.item.title" auto-ellipsis placement="top" />
          </div>
          <div class="author">
            <div class="author-container">
              <div v-if="isNotVideosPath && isNotLibraryChannelPath && isNotFoldersPath && isNotPendingAiGenerated" class="author-wrapper">
                <span v-if="props.item.ownership">{{ t('own') }}</span>
                <div v-else-if="props.item.organization_name" class="author-by" @click="handleOrganizationClicked">
                  {{ $t('library-video-by') }}
                  <span>&nbsp;</span>
                  <NewTooltip placement="top" :content="props.item.organization_name" :text-length="10" :class="{ hoverable: props.isAuthorByHoverable }" />
                </div>
                <span v-if="props.item.organization_name" class="bullet-separator">●</span>
              </div>
              <span class="duration">
                {{ getDateFormat(props.item.upload_date) }}
              </span>
              <!-- <Social v-if="!isLibraryPath" :type="SocialType.Views" :variant="SocialVariant.cardBottom" :total="props.item.views" /> -->
              <Social v-if="!isLibraryPath" :type="SocialType.Likes" :variant="SocialVariant.cardBottom" :total="props.item.likes" />
              <Social v-if="!isLibraryPath" :type="SocialType.Shares" :variant="SocialVariant.cardBottom" :total="props.item.shares" />
            </div>
          </div>
        </div>
        <div class="icons-container">
          <!-- <Social v-if="isLibraryPath" :type="SocialType.Views" :variant="SocialVariant.cardBottom" :total="props.item.views" /> -->
          <Social v-if="isLibraryPath" :type="SocialType.Likes" :variant="SocialVariant.cardBottom" :total="props.item.likes" />
          <Social v-if="isLibraryPath" :type="SocialType.Shares" :variant="SocialVariant.cardBottom" :total="props.item.shares" />
          <div v-if="displayGeoRestrictions" class="geo-restrictions">
            <GeoRestrictions :countries-allow="props.item.countries_allow" :countries-disallow="props.item.countries_disallow" layout="tag" />
          </div>
        </div>
      </div>
      <VideoComponentCategories :categories="props.item.categories" class="mt-1" />
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, onMounted, onUnmounted, reactive, ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { DropdownComponent, ImageComponent, Label, LoadingSpinner, NewTooltip, ProfileImage, VideoLayoutOrientation } from '@/components/common'
import { formatDuration, getDateFormat, getLibraryChannelHandle, openModalCustomConfirmation } from '@/services/utils'
import { MILLISECONDS_PER_SECOND, PLAYLIST_VIDEOS_MAX_NUM, TRUVID_CREATE_PLAYLIST_STATES } from '@/services/constants'
import IconWrapper from '@/components/common/icons/IconWrapper.vue'
import VideoActionButton from './VideoActionButton.vue'
import { uid } from 'uid'
import Hls from 'hls.js'
import { checkPermission } from '@/store/permissions'
import { useStore } from 'vuex'
import type { Video, VideoDraftItem } from '@/services/videos/types'
import { componentSize, useWidgetEmbedModal } from '@/composables'
import { BaseRoutes, CRUD, CustomModalProps, DataActions, DataType, SocialType, SocialVariant, VideoDetailScreenTypes, VideoEditVideoSettingsScreen } from '@/interfaces'
import { useI18n } from 'vue-i18n'
import { UPLOAD_VIDEO_NOTIFY_NEW_INJECTION_KEY, VIDEO_ACTIONS_INJECTION_KEY } from '@/constants'
import { apiService } from '@/services'
import { initAndRevealMainModal, ModalType } from '@/components/modals/modals-util'
import i18n from '@/i18n'
import { IModalProps } from '@/store/custom-modal.module'
import PlayerModal from '../modals/player-modal/PlayerModal.vue'
import GeoRestrictions from '../common/GeoRestrictions.vue'
import VideoComponentCategories from '@/components/base/video-component-tags/VideoComponentCategories.vue'
import { TagColors } from '@/components/common/tag/utils'
import IconAdd from '@/assets/common/add-icon.svg'
import IconRemove from '@/assets/common/remove-icon.svg'
import IconEmbed from '@/assets/common/embed-icon.svg'
import IconWhiteDownload from '@/assets/icons/download-white.svg'
import IconLoaderWhite from '@/assets/common/loader-white.svg'
import { useDownloadVideo } from '@/composables/useDownloadVideo'
import { useDefaultChannelProfileImages } from '@/composables/defaultChannelProfileImages'
import NewLabel from '@/components/base/video-component-tags/NewLabel.vue'
import Social from '@/components/common/video-social/Social.vue'
import { LabelColors, LabelSizes } from '@/components/common/label/utils'
import VideoNotPairedTag from '@/components/base/video-component-tags/VideoNotPairedTag.vue'
import VideoAiTag from '@/components/base/video-component-tags/VideoAiTag.vue'

interface videoProps {
  item: Video | VideoDraftItem
  selected?: boolean
  bare?: boolean
  removeEmbedButton?: boolean
  removeAddButton?: boolean
  withBottom?: boolean
  imageIdx?: number
  threeDots?: boolean
  withTitleEvents?: boolean
  hoverAnimation?: boolean
  selectOnly?: boolean
  enableClickAction?: boolean
  detailsLink?: string
  defaultCursor?: boolean
  playVideo?: boolean
  showTime?: boolean
  showOwnership?: boolean
  showTopText?: boolean
  pageRoute?: BaseRoutes
  removeLabels?: boolean
  borderRadius?: number[]
  isAuthorByHoverable?: boolean
  modalView?: boolean
  geoRestrictions?: boolean
  showOrientation?: boolean
  showNotPairedTag?: boolean
  showAiGeneratedTag?: boolean
  showNewLabel?: boolean
  isVertical?: boolean
  isCover?: boolean
  onClose?: (payload: { video_id: string }) => void
  showPendingTag?: boolean
  showDownloadButton?: boolean
  navigateToDetailsOverride?: () => void
  onBeforeNavigateDetails?: (item: Video | VideoDraftItem) => void
}
const props = withDefaults(defineProps<videoProps>(), {
  imageIdx: 1,
  withBottom: true,
  threeDots: false,
  withTitleEvents: true,
  selectOnly: false,
  hoverAnimation: true,
  enableClickAction: true,
  defaultCursor: true,
  playVideo: true,
  showTime: true,
  showOwnership: true,
  showTopText: true,
  removeLabels: false,
  isAuthorByHoverable: false,
  modalView: false,
  geoRestrictions: false,
  showOrientation: true,
  showNotPairedTag: false,
  showAiGeneratedTag: true,
  showNewLabel: true,
  isCover: false,
  showDownloadButton: false
})

const router = useRouter()
const store = useStore()
const emit = defineEmits(['close', 'video:clicked', 'on-author'])
const progressbar = ref(null)
const videoplayer = ref<InstanceType<typeof HTMLVideoElement>>(null)
const videoInterval = ref()
const playingtime = ref('0')
const shouldHoverAnimation = ref()
const isCreatingPlaylistOnAddVideoState = computed(() => store.getters['playlists/getCreatePlaylistStep']() === TRUVID_CREATE_PLAYLIST_STATES.ADD_VIDEOS)
const videoelement = ref<HTMLVideoElement | null>(null)
const showPlayer = ref(false)
const progressJump = ref(false)
const startPlaybackGuardId = ref()
const currentBorderRadius = ref([6, 0, 0, 6])
const playerOptions = {
  startLevel: 1,
  maxBufferLength: 8,
  maxMaxBufferLength: 8,
  autoPlay: true,
  testBandwidth: false,
  capLevelToPlayerSize: true,
  debug: false
}
const isLibraryPath = computed(() => router.currentRoute.value.path.includes('/library'))
// const inFolderView = computed(() => router.currentRoute.value.name !== 'videos' && router.currentRoute.value.name !== 'edit-package')
const videoUrl = ref('')
const cSize = reactive(componentSize())
const { initVideoEmbedModal } = useWidgetEmbedModal()

const isSelected = computed(() => {
  if (router.currentRoute.value.path.includes('/packages/')) {
    return !!store.state.packages?.selectedVideos?.find((v) => v.video_id === props.item.video_id)
  } else {
    return !!store.state.playlists?.selectedVideos?.find((v) => v.video_id === props.item.video_id)
  }
})
const thumbnailSource = computed(() => {
  if (!props.item?.thumbnails?.length) return ''
  const thumbnails = props.item.thumbnails
  return props.isCover && props.item.is_vertical && thumbnails[3]?.url ? thumbnails[3].url : thumbnails[0].url
})
const isNotVideosPath = computed(() => router.currentRoute.value.path !== '/videos/videos')
const isNotFoldersPath = computed(() => !router.currentRoute.value.path.includes('/videos/folders/view'))
const isNotLibraryChannelPath = computed(() => router.currentRoute.value.path !== '/videos/channel')
const isNotPendingAiGenerated = computed(() => router.currentRoute.value.path !== '/ai-generated/pending')
const libraryHandle = getLibraryChannelHandle()

const { getDefaultProfileImage } = useDefaultChannelProfileImages()
const profileImage = computed(() => {
  if (props.item.ownership) {
    return store.getters['user/getOrgProfileImage']()
  }
  return props.item.icon_image_url || getDefaultProfileImage(props.item.organization_id)
})

async function getVideoUrl() {
  const format = props.item.formats?.find((format) => {
    return format.format === 'hls'
  })

  videoUrl.value = format?.path_in_cdn ? format.path_in_cdn : ''
}

function getVideoTime() {
  if (playing.value && videoplayer.value) {
    playingtime.value = formatDuration(videoplayer.value.currentTime * 1000) // convert seconds to miliseconds
  } else {
    playingtime.value = formatDuration(props.item.duration_in_ms)
  }
}

const creatingPlaylist = computed(() => {
  return router.currentRoute.value.path.includes('/createplaylist')
})

const { t } = useI18n()

const { isDownloading, canDownload, download } = useDownloadVideo()
function onDownloadClick(): void {
  download(props.item)
}

const modalType = ModalType.VIDEOS_FOLDER_DELETE_VIDEO

const dataActions = inject<DataActions | null>(VIDEO_ACTIONS_INJECTION_KEY, null)
const newVideosToNotify = inject<Ref<string[]>>(UPLOAD_VIDEO_NOTIFY_NEW_INJECTION_KEY, ref([]))
const isNewVideo = computed(() => newVideosToNotify.value.includes(props.item.video_id))
const isAiGenerated = computed(() => props.item.source_type === 'ai_generated')
const displayGeoRestrictions = computed(() => {
  const countriesAllow = props.item.countries_allow || []
  const countriesDisallow = props.item.countries_disallow || []
  return countriesAllow.length > 0 || countriesDisallow.length > 0
})

const deleteVideo = async (params) => {
  return apiService.videos
    .deleteVideo(params.id as string)
    .then(() => {
      if (dataActions?.refreshData) dataActions.refreshData()
      if (dataActions?.clearSearch) dataActions.clearSearch()
      store.dispatch('messages/addMessage', {
        message: t('video-deleted'),
        type: 'success'
      })
    })
    .catch((err) => {
      store.dispatch('messages/addMessage', {
        message: err,
        type: 'fail'
      })
    })
}

onMounted(() => {
  shouldHoverAnimation.value = props.hoverAnimation
  getVideoUrl()
  getVideoTime()
  if (props.playVideo && videoUrl.value.length > 0) {
    videoelement.value.addEventListener('mouseleave', onMouseOut)
    videoelement.value.addEventListener('mouseenter', onMouseEnter)
  }
  cSize.addObserver(videoelement.value)
  currentBorderRadius.value = props.borderRadius ? props.borderRadius : [6, 6, 6, 6]
})

onUnmounted(() => {
  if (videoelement.value) {
    videoelement.value.removeEventListener('mouseleave', () => {
      onMouseOut()
    })
    videoelement.value.removeEventListener('mouseenter', () => {
      onMouseEnter()
    })
    clearInterval(videoInterval.value)
  }
  getVideoTime()
})

function onPlaylistAction(): void {
  const clone = JSON.parse(JSON.stringify(props.item))
  const filtered = store.state.packages.selectedVideos.filter((video) => video.video_id === clone.video_id)
  if (router.currentRoute.value.path.includes('/packages/')) {
    if (isSelected.value) {
      store.commit('packages/deselectVideo', props.item.video_id)
      return
    }
    if (!filtered.length) {
      clone.uid = uid()
      store.commit('packages/selectVideo', clone)
    }
  } else {
    if (isSelected.value) {
      store.commit('playlists/deselectVideo', props.item.video_id)
      return
    }
    const createPlaylistStep = store.getters['playlists/getCreatePlaylistStep']()
    if (!filtered.length && createPlaylistStep === TRUVID_CREATE_PLAYLIST_STATES.ADD_VIDEOS) {
      clone.uid = uid()
      store.commit('playlists/selectVideo', clone)
      if (store.state.playlists.selectedVideos.length === PLAYLIST_VIDEOS_MAX_NUM) {
        store.commit('playlists/setIsVideosAdditionAllowed', false)
      }
      const currPath = router.currentRoute.value.path
      if (!currPath.includes('createplaylist')) {
        if (currPath.includes('folders')) {
          router.push({ name: 'folder-createplaylist', query: { new: 'true' } })
        } else if (currPath.includes('/videos/videos')) {
          router.push({ name: 'videos-createplaylist', query: { new: 'true' } })
        } else {
          router.push({ name: libraryHandle ? 'createplaylist-library-channel' : 'createplaylist', query: { new: 'true' } })
        }
      }
    }
  }
}

function routeToDetailsLink(): void {
  if (props.item.video_id) {
    props.onBeforeNavigateDetails?.(props.item)
    if (router.currentRoute.value.path.includes('library') || router.currentRoute.value.path.includes('dashboard') || router.currentRoute.value.name === 'channel') {
      router.push({ name: props.detailsLink, params: { id: props.item.video_id }, query: { tab: VideoDetailScreenTypes.AddToPlaylist } })
    } else {
      router.push({ name: props.detailsLink, params: { id: props.item.video_id }, query: { tab: VideoEditVideoSettingsScreen.EditVideo } })
    }
  }
}

function detailsEmbed(): void {
  initVideoEmbedModal({
    videoId: props.item.video_id,
    videoName: props.item.title
  })
}

function openDeleteVideo() {
  openModalCustomConfirmation(
    {
      action: CRUD.Delete,
      type: DataType.Video,
      itemName: props.item.title,
      id: props.item.video_id,
      onConfirm: deleteVideo
    },
    modalType
  )
}

function clickAction() {
  if (!props.enableClickAction) return
  if (props.modalView) {
    openPlayerModal()
    return
  }
  if (props.onClose) {
    return emit('close', { video_id: props.item.video_id })
  }
  if (creatingPlaylist.value || props.selectOnly) {
    onPlaylistAction()
    return
  }
  if (props.navigateToDetailsOverride) {
    props.navigateToDetailsOverride()
    return
  }
  routeToDetailsLink()
}

async function openPlayerModal() {
  const modalProps: CustomModalProps = {
    footerProps: {
      buttonCancel: i18n.global.t('close')
    },
    extraProps: { id: props.item.video_id }
  }

  const modalConfig: IModalProps = { modalType: ModalType.PLAYER_MODAL, componentContent: markRaw(PlayerModal), props: { ...modalProps } }
  initAndRevealMainModal(modalConfig)
  await store.dispatch('modal/startLoading')
}

function handleOrganizationClicked(event: Event) {
  if (!props.item.ownership && props.isAuthorByHoverable) {
    event.stopPropagation()
    emit('on-author')
  }
}

const playing = ref(false)

async function onMouseEnter() {
  clearTimeout(startPlaybackGuardId.value)
  if (playing.value || !videoUrl.value || !videoplayer.value) {
    return
  }

  startPlaybackGuardId.value = setTimeout(() => {
    if (!window.hls) {
      window.hls = new Hls(playerOptions)
    }

    window.hls.loadSource(videoUrl.value)
    window.hls.attachMedia(videoplayer.value)

    videoInterval.value = setInterval(() => {
      if (progressbar.value && videoplayer.value) {
        const mediaDuration = isNaN(videoplayer.value.duration) || videoplayer.value.duration < 1 ? 1 : videoplayer.value.duration
        const playerPosition = isNaN(videoplayer.value.currentTime) ? 0 : videoplayer.value.currentTime
        const progressbarPosition = (playerPosition / mediaDuration) * 100

        if (progressbarPosition < progressbar.value.value) {
          progressJump.value = true
          setTimeout(() => {
            progressJump.value = false
          }, 200)
        }
        progressbar.value.value = progressbarPosition
        if (playerPosition > 0) {
          playing.value = true
          getVideoTime()
        }
      }
    }, 300)

    playing.value = false
    showPlayer.value = true
  }, MILLISECONDS_PER_SECOND)
}

function onMouseOut() {
  clearTimeout(startPlaybackGuardId.value)
  playing.value = false
  showPlayer.value = false
  if (videoplayer.value) {
    videoplayer.value.pause
  }
  if (progressbar.value) {
    progressbar.value.value = 0
  }

  clearInterval(videoInterval.value)

  playingtime.value = formatDuration(props.item.duration_in_ms)
}
</script>

<script setup lang="ts"></script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.video-player {
  z-index: $zIndex1;
  opacity: 1;
  transition: opacity 0.3s ease;
  width: 100%;
  height: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
  background-color: $black;

  &:hover {
    opacity: 1;
  }

  &.cover {
    object-fit: cover;
  }
}

progress {
  background-color: #eee;
}

progress[value] {
  appearance: none;
  /* Needed for Safari */
  border: none;
  /* Needed for Firefox */
  color: $secondary-color;
  /* Fallback to a solid color */
  position: absolute;
  width: 100%;
  bottom: 0px;
  height: 4px;
  z-index: $zIndex2;
}

/* WebKit styles */
progress[value]::-webkit-progress-value {
  background-color: $secondary-color;
  transition: width 0.3s linear;
}

/* Firefox styles */
progress::-webkit-progress-bar {
  background-color: #dadbe8 !important;
}

progress::-moz-progress-bar {
  background-color: #dadbe8 !important;
}

.jump {
  progress[value]::-webkit-progress-value {
    background-color: $secondary-color;
    transition: width 0s linear;
  }
}

.video {
  width: auto;
  height: 100%;
  position: relative;
  // display: inline-block;
  border: 2px solid transparent;
  overflow: hidden;
  transition: all 0.3s ease-in-out;

  .loading {
    height: 100%;
    width: 100%;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    transition-delay: 0s;

    &.visible {
      transition-delay: 0.4s;
      opacity: 1;
    }
  }

  .thumbnail {
    z-index: $zIndex0;
    overflow: hidden;
    height: 100%;
    width: 100%;
    object-fit: contain;
    box-shadow: 10.2043px 14.2861px 29.5926px -12.2452px rgba(20, 13, 63, 0.11);
    background: #ffffff;
    position: absolute;
    transition: opacity 1s ease;

    &._selected {
      transition: opacity 0.5s ease;
      opacity: 0.4;
      background: rgba(20, 13, 63, 0.11);
      pointer-events: none;
    }

    &.invisible {
      opacity: 0;
      pointer-events: none;
    }
  }

  .bottom-extra-video-info {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    background-color: white;
    position: absolute;
    bottom: 0px;
    z-index: $zIndex2;
    width: 100%;
    padding: 14px 18px;
    opacity: 0;
    transition: opacity 0.3s;
    transform: translateY(100%);
    border-radius: 0 0 6px 6px;
    pointer-events: none;
    .video-extra-details {
      display: flex;
      align-items: center;
      width: 100%;
      .video-extra-details-title-author-container {
        flex: 1;
        margin-left: 0.5rem;
        display: grid;
        .title-text {
          display: grid;
          grid-template-columns: 100%;
          overflow: hidden;
          width: 100%;
          font-size: 12px;
          font-weight: 400;
        }
      }
      .icons-container {
        display: flex;
      }
      .author-container {
        width: 100%;
      }
      .author-by {
        display: flex;
      }
      .profile-pic {
        flex: 0 0 auto;
      }

      .geo-restrictions {
        margin-left: 4px;
      }
    }
  }

  .author-wrapper {
    display: flex;
    gap: 4px;
  }

  .video-duration-container {
    position: absolute;
    color: #ffffff;
    background-color: #000000;
    padding: 3px 11px;
    bottom: 14px;
    right: 14px;
    font-size: 10px;
    font-weight: 700;
    border-radius: 2px;
    transition: all 1s ease;

    &.playing {
      width: fit-content;
      left: 14px;
      opacity: 0;
    }

    &.xsmall {
      display: none;
    }
  }

  .author-container {
    display: flex;
    font-size: 10px;
    gap: 0.25rem;
    width: 90%;

    .hoverable:hover {
      text-decoration-line: underline;
    }
  }
  .bullet-separator {
    font-family: 'Arial', 'Helvetica', 'Segoe UI Symbol', 'DejaVu Sans', sans-serif;
  }
  .duration {
    font-weight: 300;
    letter-spacing: 0.1px;
    white-space: nowrap;
    flex-shrink: 0;
    &.small {
      display: none;
    }
  }

  .video-info-container {
    pointer-events: none;
    z-index: $zIndex2;
    position: absolute;
    bottom: 1rem;
    padding-inline: 1rem;
    width: 100%;
    .title-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      font-weight: 700;
      line-height: normal;
      letter-spacing: 0.2px;
      text-transform: none !important;
      margin-bottom: 0.25rem;
    }
    .author-by {
      pointer-events: auto;
      display: inline-flex;
      gap: 0.25rem;
    }
  }

  &:not(._no-hover):hover {
    box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.55);
    border: none;
    transition: transform 0.15s linear;
    transform: scale(1.1);
    //border-radius: 66px;
    overflow: visible;
    border-radius: 6px 6px 0 0;

    @media (max-width: $xl) {
      transform: scale(1.1);
    }

    @media (max-width: $lg) {
      transform: scale(1.1);
    }

    // TODO: temporary solution before mobile design
    @media (max-width: $md) {
      transform: scale(1.05);
    }

    &.hoverover {
      z-index: $zIndex1000;

      .playing {
        opacity: 1;
      }
    }

    .watch {
      opacity: 1;
    }

    .bottom-extra-video-info {
      opacity: 1;
      pointer-events: auto;
      box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.45);
      .details {
        display: flex;
        justify-content: center;
      }
    }

    .video-info-container {
      z-index: $zIndex2;
      visibility: hidden;
      position: absolute;
    }

    &.hover-disabled {
      transform: scale(1.04);
      border-radius: 6px;
    }
  }

  .add-to-playlist-btn {
    z-index: $zIndex2;
    position: absolute;
    right: 18px;
    top: 12px;
    display: flex;
    gap: 8px;
    flex-direction: column-reverse;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .title {
    text-align: left;
    font-weight: 800;
    font-size: 14px;
    line-height: 17px;
    /* or 121% */
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    &.top {
      color: #ffffff;
      font-size: 14px;
    }

    &.xsmall {
      display: none;
    }
  }

  .author {
    text-align: left;
    font-weight: 300;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.1px;

    &.top {
      color: #ffffff;
      font-size: 10px;
    }
    .author-span {
      text-transform: capitalize;
    }
    &.xsmall {
      display: none;
    }
    &.small,
    &.xsmall {
      .duration,
      .bullet-separator {
        display: none;
      }
    }

    .bullet-point {
      margin: 0 5px;
    }
  }

  .labels {
    z-index: $zIndex2;
    top: 0.75rem;
    padding-inline: 1rem;
    position: absolute;
    font-weight: 600;
    font-size: 10px;
    line-height: 14px;
    width: calc(100% - 45px);
    letter-spacing: 0.1px;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 5px;
    .categories {
      &.xsmall {
        display: none;
      }
    }
    .video-tags {
      display: flex;
      flex-direction: row;
      gap: 10px;
    }
    &.bare {
      width: 100%;
    }
    .label {
      padding: 3px 8px;
      border-radius: 2px;
      width: fit-content;
      background: #000000;
      border-radius: 2px;
      margin-right: 5px;
      margin-bottom: 8px;
      &.small {
        display: none;
      }
    }

    /* white */
  }

  .watch {
    position: absolute;
    width: 87px;
    height: 30px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: #fb2265;
    border-radius: 29.4526px;
    opacity: 0;
    margin: 0;
  }
  .left-tags {
    display: flex;
    gap: 0.625rem;
  }
}

.cursor {
  cursor: pointer !important;
}

.pending-label {
  position: absolute;
  right: 14px;
}
</style>
